import styled from 'styled-components';
import Table from 'rc-table';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import React from 'react';
import { BaseComponent } from '../../../components/BaseComponent';
import { HBox } from '../../../components/Box';
import { Button } from '../../../components/Buttons';
import { selectProductFilter, selectProducts } from '../selectors';
import { formatCurrency } from '../../../utils/string';
import { ProductOverviewItemActions } from './ProductOverviewActions';
import { INVOICE_DIRECTIONS } from '../../Invoices/constants';

const Container = styled.div`
  width: 100%;
  height: ${props => (props.productInvoicePage ? '100%' : '')};
  background: white;
`;

const CustomTable = styled(Table)`
  width: 100%;
  height: ${props => (props.productInvoicePage ? '100%' : '')};
  display: flex;
  flex-direction: column;
  justify-content: ${props => (props.productInvoicePage ? 'space-between' : '')};
  font-size: 12px;

  .rc-table-container {
    width: 100%;
    overflow-y: auto;
  }

  .rc-table-content table {
    width: 100%;
  }

  .rc-table-thead {
    background-color: #eff1f7;
    font-weight: 600;
    height: 52px;
    padding: 16px 0;
    margin-left: 8px;
    font-size: 14px;
  }

  .rc-table-thead {
    background-color: #eff1f7;
    font-weight: 600;
    height: 52px;
    padding: 16px 0;
    margin-left: 8px;
    font-size: 14px;
  }

  .rc-table-footer {
    background-color: #eff1f7;
    font-weight: 600;
    height: 52px;
    padding: 0 16px;
    margin-left: 8px;
    font-size: 14px;
  }

  td,
  th {
    padding: 8px 12px;
  }
`;

const IconWrapper = styled.div`
  color: ${props =>
    props?.color === 'darkgray' ? 'darkgray' : 'green' ? '#11ab79' : props?.color === 'orange' ? '#FC9629' : '#d50046'};
`;

const ActionsContainer = styled.div`
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const StatusLabel = styled.span`
  padding: 2px;
  text-align: center;
  border-radius: 5px;
  color: #fff;
  background-color: ${props => {
    if (props.status == '0') {
      return 'rgb(21, 172, 121)';
    }
    if (props.status == '1') {
      return '#D50046';
    }
    if (props.status == '2') {
      return 'black';
    }
  }};
`;

/* eslint-disable react/prefer-stateless-function */
class ProductsOverviewTableComp extends BaseComponent {
  constructor(props) {
    super(props);
  }

  get productsToShow() {
    let products = this.props.products;

    if (this.props.filter?.status !== '2') {
      products = this.props.products?.filter(p => p?.status !== '2');
    }
    return products;
  }

  get products() {
    return this.props.productInvoicePage ? this.props.productSubjects : this.productsToShow;
  }

  formatDate = dateString => {
    if (!dateString) return '-';
    return dayjs(dateString).format('DD-MM-YYYY');
  };

  getData = product => {
    const name = `${product?.name ?? 'product'}`;
    const description = product?.description ?? '';
    const purchasePriceExclVat = formatCurrency(product?.purchasePriceExclVat) || formatCurrency('0.00');
    const salesPriceExclVat = formatCurrency(product?.salesPriceExclVat) || formatCurrency('0.00');
    let originalPrice = '0';
    if (this.props.direction == INVOICE_DIRECTIONS.INCOMING) {
      originalPrice = product?.purchasePriceExclVat;
    }
    if (this.props.direction == INVOICE_DIRECTIONS.OUTGOING) {
      originalPrice = product?.salesPriceExclVat;
    }
    const createdAt = this.formatDate(product?.createdAt);
    const quantity = product.quantity || 0;
    const totalPrice = product?.totalPriceExclVat;
    let type = '';
    if (product?.isCounter) {
      type = this.lbl('product.column.type.counter');
    }
    if (product?.isOneTime) {
      type = this.lbl('product.column.type.one_time');
    }

    const statusColors = {
      '0': 'ACTIVE',
      '1': 'DE-ACTIVE',
      '2': 'DELETED',
    };

    return {
      name,
      description,
      purchasePriceExclVat,
      salesPriceExclVat,
      originalPrice,
      quantity,
      totalPrice,
      createdAt,
      type,
      status: {
        value: product?.status,
        label: statusColors[product?.status],
      },
      counter: product?.counter,
      id: product?.id,
      actions: product,
      delete: {
        subjectId: product?.id,
        invoiceId: this.props.invoiceId,
      },
      rowColor: name.includes('uurtarief') || name.includes('m3') || name.includes('doorbelasting') || name.includes('nacalculatie')  ? 'red' : 'inherit', // Add row color logic
    };
  };

  getColumns = () => {
    if (this.props.productInvoicePage) {
      let columns = [
        { title: this.lblStr('invoice.description'), dataIndex: 'name', key: 'name', align: 'left' },
        // { dataIndex: 'description', key: 'description', align: 'left' },
        {
          title: this.lblStr('product.invoice-modal.product.piece_excl_vat'),
          dataIndex: 'originalPrice',
          key: 'originalPrice',
          align: 'left',
          render(value) {
            return formatCurrency(value);
          },
        },
        {
          title: this.lblStr('product.quantity'),
          dataIndex: 'quantity',
          key: 'quantity',
          align: 'left',
        },
        {
          title: this.lblStr('product.invoice-modal.total_price_excl_vat'),
          dataIndex: 'totalPrice',
          key: 'totalPrice',
          align: 'left',
          render(value) {
            return formatCurrency(value);
          },
        },
        {
          title: this.lblStr('label.delete'),
          dataIndex: 'delete',
          key: 'delete',
          align: 'left',
          render(value) {
            return (
              <ActionsContainer>
                <IconWrapper color={'darkgray'}>
                  <ProductOverviewItemActions
                    action={'productSubjectDelete'}
                    product={value?.subjectId}
                    invoiceId={value?.invoiceId}
                  />
                </IconWrapper>
              </ActionsContainer>
            );
          },
        },
      ];

      return columns;
    }
    return [
      { title: this.lblStr('product.column.name'), dataIndex: 'name', key: 'name', align: 'left' },
      {
        title: this.lblStr('product.create.purchase_price_excl_vat'),
        dataIndex: 'purchasePriceExclVat',
        key: 'purchasePriceExclVat',
        align: 'left',
      },
      {
        title: this.lblStr('product.create.sales_price_excl_vat'),
        dataIndex: 'salesPriceExclVat',
        key: 'salesPriceExclVat',
        align: 'left',
      },
      {
        title: this.lblStr('product.column.created_at'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        align: 'left',
      },
      {
        title: this.lblStr('product.column.type'),
        dataIndex: 'type',
        key: 'type',
        align: 'left',
      },
      {
        title: this.lblStr('product.column.counter'),
        dataIndex: 'counter',
        key: 'counter',
        align: 'left',
      },
      {
        title: this.lblStr('product.column.status'),
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        render(value) {
          return <StatusLabel status={value?.value}>{value?.label}</StatusLabel>;
        },
      },
      {
        title: this.lblStr('product.column.actions'),
        key: 'actions',
        dataIndex: 'actions',
        align: 'left',
        render(value) {
          return (
            <ActionsContainer>
              <IconWrapper color={'darkgray'}>
                <ProductOverviewItemActions product={value} />
              </IconWrapper>
            </ActionsContainer>
          );
        },
      },
    ];
  };

  to(url, newWindow) {
    //
    if (newWindow) {
      window.open(url);
    } else {
      this.props.history.push(url);
    }
  }

  tofn(url) {
    return event => {
      this.to(url, event?.ctrlKey || event?.metaKey);
    };
  }

  render() {
    const columns = this.getColumns();
    const data = this.products?.map(product => this.getData(product));
    console.log(this.props.filter, 'FILTER');
    return (
      <Container productInvoicePage={this.props.productInvoicePage}>
        <CustomTable
          productInvoicePage={this.props.productInvoicePage}
          style={{ overflow: 'nonde' }}
          columns={columns}
          data={data}
          scroll={{ x: window.innerWidth < 975 ? 975 : false }}
          emptyText="No products found by current filters"
          onRow={(record, index) => {
            return {
              style: { color: record.rowColor }, // Apply red background if name contains BPM
              onClick: this.props.productInvoicePage
                ? () => (this.props.disabled ? {} : this.props.addProduct('edit', record?.id))
                : this.tofn(`/products/${this.props.products[index]?.id}`),
              product: this.props.products[index],
            };
          }}
          footer={currentData => {
            if (!this.props.productInvoicePage) return null;
            const total = currentData?.map(item => item?.totalPrice)?.reduce((acc, current) => acc + current, 0);
            return (
              <>
                <HBox vCenter>
                  <Button onClick={this.props.addProduct} small disabled={this.props.disabled}>
                    {this.lbl('add')}
                  </Button>
                  <p>{this.lbl('product.invoice-modal.total_price_excl_vat')}: </p>
                  {formatCurrency(total)}
                </HBox>
              </>
            );
          }}
        />
      </Container>
    );
  }
}

ProductsOverviewTableComp.propTypes = {
  dispatch: PropTypes.func.isRequired,
  onItemClick: PropTypes.func,
};

const mapStateToProps = (state, ownProps) =>
  createStructuredSelector({
    products: selectProducts(),
    filter: selectProductFilter(),
  });

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatch,
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export const ProductsOverviewTable = compose(withConnect, withRouter, injectIntl)(ProductsOverviewTableComp);
